import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import SEO, { SEO_CONFIG } from '@/components/SEO';
import { Upload, CheckCircle2, AlertCircle, ArrowRight, LogIn } from 'lucide-react';

// Use relative path for API calls to avoid CORS issues
const API = '/api';

const BecomeInstructor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [existingApplication, setExistingApplication] = useState(null);
  const [isInstructor, setIsInstructor] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [step, setStep] = useState(1);
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    expertise_area: '',
    bio: '',
    iban: '',
    national_id: '',
    certificate_url: '',
    id_document_url: ''
  });

  const [uploadingCert, setUploadingCert] = useState(false);
  const [uploadingId, setUploadingId] = useState(false);

  // Check user status and existing application
  useEffect(() => {
    const checkUserStatus = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setIsLoggedIn(false);
        setCheckingStatus(false);
        return;
      }

      setIsLoggedIn(true);

      try {
        // First check if user is already an approved instructor
        const meResponse = await axios.get(`${API}/instructor/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // If we get here, user is an approved instructor
        setIsInstructor(true);
        setCheckingStatus(false);
        return;
      } catch (error) {
        // 403 means not an instructor, continue to check application
        if (error.response?.status !== 403) {
          console.error('Check instructor error:', error);
        }
      }

      try {
        // Check for existing application
        const appResponse = await axios.get(`${API}/instructor/my-application`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (appResponse.data.has_application) {
          setExistingApplication(appResponse.data.application);
        }
      } catch (error) {
        console.error('Check application error:', error);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkUserStatus();
  }, []);

  const handleFileUpload = async (file, docType) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Lütfen önce giriş yapın');
      navigate('/login');
      return null;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Sadece PDF, JPG, JPEG veya PNG dosyaları yüklenebilir');
      return null;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya boyutu 10MB\'dan küçük olmalıdır');
      return null;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/upload/document?doc_type=${docType}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      return response.data.file_url;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Dosya yüklenirken hata oluştu');
      return null;
    }
  };

  const handleCertificateUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingCert(true);
    const fileUrl = await handleFileUpload(file, 'certificates');
    if (fileUrl) {
      setFormData({ ...formData, certificate_url: fileUrl });
      toast.success('Sertifika başarıyla yüklendi');
    }
    setUploadingCert(false);
  };

  const handleIdUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingId(true);
    const fileUrl = await handleFileUpload(file, 'id_documents');
    if (fileUrl) {
      setFormData({ ...formData, id_document_url: fileUrl });
      toast.success('Kimlik belgesi başarıyla yüklendi');
    }
    setUploadingId(false);
  };

  const validateStep1 = () => {
    if (!formData.full_name || !formData.email || !formData.phone || !formData.expertise_area) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return false;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Geçerli bir email adresi girin');
      return false;
    }

    return true;
  };

  const validateStep2 = () => {
    // IBAN validation
    const iban = formData.iban.replace(/\s/g, '').toUpperCase();
    if (!iban.startsWith('TR') || iban.length !== 26) {
      toast.error('IBAN TR ile başlamalı ve 26 karakter olmalıdır');
      return false;
    }

    // TCKN validation
    const tckn = formData.national_id.replace(/\s/g, '');
    if (tckn.length !== 11 || !/^\d+$/.test(tckn)) {
      toast.error('TC Kimlik No 11 haneli olmalıdır');
      return false;
    }

    if (!formData.certificate_url) {
      toast.error('Lütfen sertifika belgenizi yükleyin');
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep2()) return;

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Lütfen önce giriş yapın');
        navigate('/login');
        return;
      }

      // Create JSON payload for submission
      const submitData = {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        expertise_area: formData.expertise_area,
        bio: formData.bio || '',
        iban: formData.iban,
        national_id: formData.national_id,
        certificate_url: formData.certificate_url,
        id_document_url: formData.id_document_url || null
      };

      await axios.post(`${API}/instructor/apply`, submitData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success('Başvurunuz başarıyla gönderildi! İnceleme süreci başladı.');
      setTimeout(() => navigate('/application-status'), 2000);
    } catch (error) {
      console.error('Application error:', error);
      toast.error(error.response?.data?.detail || 'Başvuru gönderilirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking status
  if (checkingStatus) {
    return (
      <div className="min-h-screen pt-20 wellness-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // If not logged in, show login prompt
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen pt-20 wellness-gradient">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="text-center">
            <CardHeader>
              <CardTitle className="text-2xl">Eğitmen Olmak İster Misiniz?</CardTitle>
              <CardDescription>
                Eğitmen başvurusu yapmak için önce giriş yapmanız veya kayıt olmanız gerekiyor.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600">
                Pilatika'da eğitmen olun, kurslarınızı satın ve binlerce kullanıcıya ulaşın.
              </p>
              <div className="flex gap-3 justify-center">
                <Button 
                  variant="outline"
                  onClick={() => navigate('/login')}
                >
                  <LogIn className="mr-2 h-4 w-4" />
                  Giriş Yap
                </Button>
                <Button 
                  className="btn-primary"
                  onClick={() => navigate('/register?role=instructor')}
                >
                  Eğitmen Olarak Kayıt Ol
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // If user is already an approved instructor, redirect to dashboard
  if (isInstructor) {
    return (
      <div className="min-h-screen pt-20 wellness-gradient">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="text-center">
            <CardHeader>
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl">Zaten Eğitmensiniz!</CardTitle>
              <CardDescription>
                Eğitmen hesabınız aktif. Eğitmen panelinize giderek kurslarınızı yönetebilirsiniz.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button 
                className="btn-primary"
                onClick={() => navigate('/instructor/dashboard')}
              >
                Eğitmen Paneline Git
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // If user already has an application, redirect to status page
  if (existingApplication) {
    const statusMessages = {
      pending: 'Başvurunuz inceleniyor.',
      approved: 'Tebrikler! Başvurunuz onaylandı.',
      rejected: 'Başvurunuz reddedildi.',
      correction_requested: 'Başvurunuzda düzeltme gerekiyor.'
    };

    return (
      <div className="min-h-screen pt-20 wellness-gradient">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="text-center">
            <CardHeader>
              <CardTitle className="text-2xl">Mevcut Başvurunuz Var</CardTitle>
              <CardDescription>
                {statusMessages[existingApplication.status] || 'Başvurunuz işleniyor.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600">
                Başvuru durumunuzu takip etmek ve detayları görmek için aşağıdaki butona tıklayın.
              </p>
              <Button 
                className="btn-primary"
                onClick={() => navigate('/application-status')}
              >
                Başvuru Durumunu Gör
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 wellness-gradient">
      <SEO {...SEO_CONFIG.becomeInstructor} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Eğitmen Ol</h1>
          <p className="text-lg text-gray-600">
            Pilatika'ya katılın ve binlerce kullanıcıya ulaşın
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center ${step >= 1 ? 'text-purple-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>
                {step > 1 ? <CheckCircle2 className="w-6 h-6" /> : '1'}
              </div>
              <span className="ml-2 font-medium">Kişisel Bilgiler</span>
            </div>
            <div className="w-12 h-0.5 bg-gray-300"></div>
            <div className={`flex items-center ${step >= 2 ? 'text-purple-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>
                2
              </div>
              <span className="ml-2 font-medium">Belgeler & IBAN</span>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {step === 1 ? 'Kişisel Bilgileriniz' : 'Belgeler ve Ödeme Bilgileri'}
            </CardTitle>
            <CardDescription>
              {step === 1 ? 'Lütfen bilgilerinizi eksiksiz doldurun' : 'Sertifika ve IBAN bilgilerinizi ekleyin'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {step === 1 && (
                <>
                  <div>
                    <Label htmlFor="full_name">Ad Soyad *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="Ahmet Yılmaz"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="email@example.com"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Telefon *</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="05XX XXX XX XX"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Uzmanlık Alanı *</Label>
                    <Select
                      value={formData.expertise_area}
                      onValueChange={(value) => setFormData({ ...formData, expertise_area: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seçiniz" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pilates">Pilates</SelectItem>
                        <SelectItem value="yoga">Yoga</SelectItem>
                        <SelectItem value="fitness">Fitness</SelectItem>
                        <SelectItem value="nutrition">Beslenme</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="bio">Kısa Biyografi (Opsiyonel)</Label>
                    <Textarea
                      id="bio"
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      placeholder="Kendinizden bahsedin..."
                      rows={4}
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button type="button" onClick={handleNext} className="btn-primary">
                      Devam Et
                    </Button>
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <div>
                    <Label htmlFor="iban">IBAN *</Label>
                    <Input
                      id="iban"
                      value={formData.iban}
                      onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                      placeholder="TR XX XXXX XXXX XXXX XXXX XXXX XX"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">TR ile başlamalı, 26 karakter olmalıdır</p>
                  </div>

                  <div>
                    <Label htmlFor="national_id">TC Kimlik No *</Label>
                    <Input
                      id="national_id"
                      value={formData.national_id}
                      onChange={(e) => setFormData({ ...formData, national_id: e.target.value })}
                      placeholder="XXXXXXXXXXX"
                      maxLength={11}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">11 haneli TC kimlik numaranız</p>
                  </div>

                  <div>
                    <Label>Sertifika/Belge Yükle *</Label>
                    <div className="mt-2">
                      <label className="flex items-center justify-center w-full h-32 px-4 transition border-2 border-gray-300 border-dashed rounded-md appearance-none cursor-pointer hover:border-purple-400 focus:outline-none">
                        <div className="flex flex-col items-center space-y-2">
                          {formData.certificate_url ? (
                            <>
                              <CheckCircle2 className="w-8 h-8 text-green-600" />
                              <span className="text-sm text-green-600">Sertifika yüklendi</span>
                            </>
                          ) : uploadingCert ? (
                            <>
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                              <span className="text-sm">Yükleniyor...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="w-8 h-8 text-gray-400" />
                              <span className="text-sm text-gray-600">PDF, JPG, PNG (Max 10MB)</span>
                            </>
                          )}
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleCertificateUpload}
                          disabled={uploadingCert}
                        />
                      </label>
                    </div>
                  </div>

                  <div>
                    <Label>Kimlik Belgesi (Opsiyonel)</Label>
                    <div className="mt-2">
                      <label className="flex items-center justify-center w-full h-32 px-4 transition border-2 border-gray-300 border-dashed rounded-md appearance-none cursor-pointer hover:border-purple-400 focus:outline-none">
                        <div className="flex flex-col items-center space-y-2">
                          {formData.id_document_url ? (
                            <>
                              <CheckCircle2 className="w-8 h-8 text-green-600" />
                              <span className="text-sm text-green-600">Kimlik belgesi yüklendi</span>
                            </>
                          ) : uploadingId ? (
                            <>
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                              <span className="text-sm">Yükleniyor...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="w-8 h-8 text-gray-400" />
                              <span className="text-sm text-gray-600">PDF, JPG, PNG (Max 10MB)</span>
                            </>
                          )}
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleIdUpload}
                          disabled={uploadingId}
                        />
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <Button type="button" variant="outline" onClick={() => setStep(1)}>
                      Geri
                    </Button>
                    <Button type="submit" className="btn-primary" disabled={loading}>
                      {loading ? 'Gönderiliyor...' : 'Başvuruyu Gönder'}
                    </Button>
                  </div>
                </>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Info Cards */}
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-2">📚 Ders Oluşturun</h3>
              <p className="text-sm text-gray-600">
                Video derslerinizi yükleyin ve binlerce kullanıcıya ulaşın
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-2">💰 Kazanç Elde Edin</h3>
              <p className="text-sm text-gray-600">
                Her satıştan %80 kazanç elde edin
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-2">📊 İstatistikler</h3>
              <p className="text-sm text-gray-600">
                Satışlarınızı ve kazançlarınızı takip edin
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BecomeInstructor;
